/**
 * ChatSurface — the reusable chat-first panel for management screens
 * (Phase 1 · chat-first correction batch · item 2).
 *
 * Chat is PRIMARY on every management surface (Lead Inbox, Ancillary, …).
 * The existing forms become the manual-FALLBACK layer beneath it. This
 * component mirrors the Project Conversation behaviour (verbatim message
 * + AI-derived proposals, FK to the source message, chip-by-default /
 * bubble-only-when-asking, internal-note split) using the PURE
 * lib/localChat model so it works on lead/ancillary surfaces that are not
 * yet Firestore-backed.
 *
 * AI→data: each proposal's "Apply" hands its `patch` to the parent via
 * `onApply` — the parent maps it onto its fallback form state. So a staff
 * member can drive the whole surface by chatting; the form is the
 * fallback they fall back to, not the primary path.
 *
 * Pure-white surfaces, distinction via borders + typography (locked).
 */
import { useMemo, useRef, useState } from 'react';
import {
  applyProposal,
  dismissProposal,
  emptyChat,
  logClientSend,
  openProposals,
  postChatAgent,
  postChatRemote,
  promoteToClient,
  transcribeInAppVoice,
  visibleChat,
  type ChatProposal,
  type ChatState,
  type ChatSurfaceKind,
  type ScreenCtx,
} from '../lib/localChat';
import { useVoiceCapture } from '../lib/useVoiceCapture';

export interface ChatSurfaceProps {
  /** What this conversation is about (header line). */
  title: string;
  /** Sub-line — e.g. "Chat is primary · the form below is the fallback". */
  note?: string;
  /** Signed-in person (author of staff messages). */
  authorName: string;
  /** Team view sees internal notes; client view never does. */
  isTeamView: boolean;
  /**
   * AI→data sink. Called when staff applies a proposal — the parent maps
   * `patch` onto its fallback form / state. Return value ignored.
   */
  onApply: (patch: Record<string, unknown>) => void;
  /** Optional seed messages (e.g. an opening system line). */
  seed?: ChatState;
  /**
   * Which surface this chat sits on — light context for the REAL Claude
   * extractor (Round 2). Defaults to 'lead'.
   */
  surface?: ChatSurfaceKind;
  /** Current fallback-form state — extra context for the extractor. */
  formState?: Record<string, unknown>;
  /**
   * Conversational agent mode (owner direction 2026-05-18). When set, the
   * surface runs the ONE conversational agent (avbpAgentChat): the user
   * chats and the agent REPLIES IN NATURAL LANGUAGE (taking real actions
   * server-side). No chips. Off → the legacy chip-extractor path (kept as
   * the manual fallback). `projectId`/`threadId` scope the agent + post.
   */
  agentMode?: boolean;
  projectId?: string;
  threadId?: string;
  /**
   * GLOBAL screen-contextual chat (2026-05-18b). When the surface is the
   * global dock, this carries the route-derived scope + label + entity id
   * so the ONE agent answers/acts about whichever screen the user is on.
   */
  screenCtx?: ScreenCtx;
  /**
   * Best-effort: the agent may return a deep-link route; the dock passes a
   * navigate handler so the UI can follow it (minimum-acceptable is screen
   * context in + contextual label; navigation is wired when present).
   */
  onNavigate?: (route: string) => void;
  /** Render compactly for the floating dock (vs an embedded panel). */
  docked?: boolean;
  /**
   * Whether the "Forward to client ↗" button should be shown on internal
   * messages. Defaults to `true` for embedded chat panels that sit on a
   * specific contact/project screen. Set to `false` on global/list screens
   * (Contacts, Leads, Team Inbox, etc.) where no single client is in context —
   * forwarding is meaningless there. (AD-005, Fix 4 · 2026-06-17)
   */
  canForwardToClient?: boolean;
}

export default function ChatSurface({
  title,
  note,
  authorName,
  isTeamView,
  onApply,
  seed,
  surface = 'lead',
  formState,
  agentMode = false,
  projectId,
  threadId,
  screenCtx,
  onNavigate,
  docked = false,
  canForwardToClient = true,
}: ChatSurfaceProps) {
  const [state, setState] = useState<ChatState>(() => seed ?? emptyChat());
  const [text, setText] = useState('');
  const [thinking, setThinking] = useState(false);
  const [offlineMode, setOfflineMode] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceMsg, setVoiceMsg] = useState('');
  const seqRef = useRef(1);
  const streamRef = useRef<HTMLDivElement>(null);
  const voice = useVoiceCapture();

  const msgs = useMemo(
    () => visibleChat(state.messages, { isTeamView }),
    [state.messages, isTeamView],
  );
  const open = useMemo(() => openProposals(state), [state]);
  // Proposals are TEAM-ONLY (the AI/review layer — same as the spine).
  // In conversational agent mode there are NO chips at all — the agent
  // replies in natural language and acts server-side.
  const showProps = agentMode || !isTeamView ? [] : open;

  /**
   * AD-005: chat is INTERNAL-BY-DEFAULT. A team member's composed message is
   * team-only unless they consciously choose "Send to client" (`toClient`).
   * A client's own message is naturally client-visible (it IS the client
   * track). So `internal` = true only for a team message NOT sent to client.
   */
  async function send(override?: string, toClient = false) {
    const body = (override ?? text).trim();
    if (!body || thinking) return;
    const seq = seqRef.current++;
    if (override === undefined) setText('');
    setThinking(true);
    const ts = Date.now();
    const messageId = `cm-${String(seq).padStart(4, '0')}`;
    // Team messages default to internal; "Send to client" flips it off. A
    // client's own messages are always client-visible.
    const isInternal = isTeamView ? !toClient : false;
    try {
      if (agentMode) {
        // The ONE conversational agent: chat in → natural reply (+ real
        // server-side actions). No chips. seq+1 is consumed by the agent
        // reply bubble, so advance the counter by 2.
        seqRef.current += 1;
        const res = await postChatAgent(
          state,
          {
            authorName,
            authorRole: isTeamView ? 'staff' : 'client',
            body,
            internal: isInternal,
            ts,
          },
          seq,
          {
            projectId,
            threadId,
            // GLOBAL screen-contextual chat: tell the agent where we are.
            screen: screenCtx?.screen,
            screenLabel: screenCtx?.screenLabel,
            screenEntityId: screenCtx?.screenEntityId,
          },
        );
        setState(res.state);
        setOfflineMode(res.fallback);
        // Best-effort: follow an agent-emitted deep-link if the host
        // provided a navigate handler (minimum-acceptable does not require
        // this; we wire it when present).
        if (res.navigateTo && onNavigate) onNavigate(res.navigateTo);
      } else {
        // Legacy chip-extractor (manual fallback path). Real Claude
        // extraction by default; transparently falls back to the offline
        // keyword stub if the CF is unreachable.
        const res = await postChatRemote(
          state,
          {
            authorName,
            authorRole: isTeamView ? 'staff' : 'client',
            body,
            internal: isInternal,
            ts,
          },
          seq,
          { surface, formState },
        );
        setState(res.state);
        setOfflineMode(res.usedFallback);
      }
      // AD-005 step 7: a conscious client-send is audited in the background
      // (visibility + audit ONLY — no live channel send in Module 1).
      if (isTeamView && toClient) {
        logClientSend({ actor: authorName, messageId, body, via: 'compose', ts });
      }
    } finally {
      setThinking(false);
      requestAnimationFrame(() => {
        const el = streamRef.current;
        if (el) el.scrollTop = el.scrollHeight;
      });
    }
  }

  function apply(p: ChatProposal) {
    onApply(p.patch);
    setState((s) => applyProposal(s, p.id));
  }
  function dismiss(p: ChatProposal) {
    setState((s) => dismissProposal(s, p.id));
  }

  /**
   * AD-005: "Forward to client" on an already-posted internal message —
   * promotes that one message to client-visible (a conscious act, team-view
   * only) and audits it. No live channel send in Module 1.
   */
  function forwardToClient(m: { id: string; body: string }) {
    setState((s) => promoteToClient(s, m.id));
    logClientSend({
      actor: authorName,
      messageId: m.id,
      body: m.body,
      via: 'forward',
      ts: Date.now(),
    });
  }

  /**
   * In-app voice: record (MediaRecorder) → transcribe via the SAME locked
   * Whisper path WhatsApp uses → feed the transcript to the agent exactly
   * like a typed message. Tap to start, tap again to stop+send. Graceful
   * on permission denial (the hook returns a clear human message).
   */
  async function toggleVoice() {
    if (voice.status === 'recording') {
      const blob = await voice.stop();
      if (!blob) {
        setVoiceMsg('Nothing was recorded — try again or type instead.');
        return;
      }
      setVoiceBusy(true);
      setVoiceMsg('Transcribing your voice note…');
      try {
        const r = await transcribeInAppVoice(blob, {
          mimeType: blob.type,
        });
        if (r.ok && r.transcript) {
          setVoiceMsg('');
          // Feed the transcript to the agent exactly like a typed message
          // (raw audio is the canonical input — verbatim-sacrosanct).
          await send(r.transcript);
        } else if (r.skipped) {
          setVoiceMsg(
            'Voice transcription is not configured right now — please type your message.',
          );
        } else {
          setVoiceMsg(
            'Could not transcribe that — please try again or type your message.',
          );
        }
      } finally {
        setVoiceBusy(false);
      }
      return;
    }
    setVoiceMsg('');
    await voice.start();
  }

  const asks = showProps.filter((p) => p.asksUser);
  const ambient = showProps.filter((p) => !p.asksUser);

  const voiceState =
    voice.status === 'recording'
      ? 'recording'
      : voiceBusy
        ? 'busy'
        : 'idle';
  const voiceHint = voice.message || voiceMsg;

  return (
    <section className={`chatsurface${docked ? ' chatsurface--docked' : ''}`}>
      <header className="cs-head">
        <span className="cs-title">{title}</span>
        {note && <span className="cs-note">{note}</span>}
        {offlineMode && (
          <span
            className="cs-note"
            title="Claude was unreachable — using the built-in offline keyword extractor. Proposals still work."
          >
            offline mode (keyword fallback)
          </span>
        )}
      </header>

      <div className="cs-stream" ref={streamRef}>
        {msgs.length === 0 && (
          <div className="cs-empty">
            The record beside this is the source of truth. Type what you
            know here in plain language and the AI proposes changes — they
            land in the record only after you confirm.
          </div>
        )}
        {msgs.map((m) => {
          // AD-005 badges: team view distinguishes every message at a glance.
          // internal = team-only; non-internal = consciously sent to client.
          const isAssistant = m.authorName === 'AV Assistant';
          return (
            <div key={m.id} className={`cs-msg${m.internal ? ' internal' : ''}`}>
              <div className="cs-who">
                {m.authorName}
                {isTeamView &&
                  (m.internal ? (
                    <span className="cs-tag cs-tag-int">internal</span>
                  ) : (
                    <span className="cs-tag cs-tag-cli">sent to client</span>
                  ))}
              </div>
              <div className="cs-bub">{m.body}</div>
              {/* Forward to client: team-view only, on an internal message
                  that a human posted (not the assistant's own reply), AND
                  only on screens that have a specific client context
                  (canForwardToClient=false on global list screens). */}
              {isTeamView && m.internal && !isAssistant && canForwardToClient && (
                <button
                  type="button"
                  className="cs-fwd"
                  onClick={() => forwardToClient(m)}
                  title="Promote this message so the client can see it"
                >
                  Forward to client →
                </button>
              )}
            </div>
          );
        })}
        {thinking && (
          <div className="cs-msg">
            <div className="cs-who">{agentMode ? 'AV Assistant' : 'AI'}</div>
            <div className="cs-bub">
              {agentMode ? 'Thinking…' : 'Reading that and proposing actions…'}
            </div>
          </div>
        )}
      </div>

      {/* AI→data strip (TEAM-ONLY). asksUser → bordered bubble; else chip. */}
      {showProps.length > 0 && (
        <div className="cs-ai">
          {asks.map((p) => (
            <div className="cs-bubble" key={p.id}>
              <div className="cs-bh">
                <span className="cs-k">AI</span> Needs your call
              </div>
              <p>
                <b>{p.title}</b> — {p.detail}
              </p>
              <div className="cs-acts">
                <button className="cs-mini pri" onClick={() => apply(p)}>
                  Apply
                </button>
                <button className="cs-mini" onClick={() => dismiss(p)}>
                  Not now
                </button>
              </div>
            </div>
          ))}
          {ambient.map((p) => (
            <span className="cs-chip" key={p.id}>
              <span className="cs-k">AI</span>
              <span>
                <b>{p.title}</b> — {p.detail}
              </span>
              <button className="cs-lnk" onClick={() => apply(p)}>
                Apply
              </button>
              <button className="cs-lnk gh" onClick={() => dismiss(p)}>
                Dismiss
              </button>
            </span>
          ))}
        </div>
      )}

      {voiceHint && (
        <div className="cs-voicehint" role="status">
          {voiceHint}
        </div>
      )}
      <div className="cs-composer">
        {agentMode && (
          <button
            type="button"
            className={`cs-mic${
              voiceState === 'recording' ? ' rec' : ''
            }`}
            disabled={thinking || voiceBusy}
            onClick={toggleVoice}
            aria-pressed={voiceState === 'recording'}
            title={
              voiceState === 'recording'
                ? 'Stop & send voice note'
                : 'Record a voice note (transcribed and answered)'
            }
            aria-label={
              voiceState === 'recording'
                ? 'Stop and send voice note'
                : 'Record a voice note'
            }
          >
            {voiceState === 'recording'
              ? '■'
              : voiceState === 'busy'
                ? '…'
                : '🎙'}
          </button>
        )}
        <textarea
          className="cs-ti"
          rows={1}
          placeholder="Message — describe it in plain language; the AI proposes the actions…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
        />
        {/* AD-005: in team view the default Send posts an INTERNAL (team-only)
            message — it can never accidentally reach the client. "Send to
            client →" is the distinct, deliberate act that delivers it. */}
        <button
          className="cs-send"
          disabled={!text.trim() || thinking}
          onClick={() => send()}
          title={isTeamView ? 'Send (team-only — internal)' : 'Send'}
        >
          {thinking ? '…' : '↑'}
        </button>
        {isTeamView && (
          <button
            type="button"
            className="cs-send2client"
            disabled={!text.trim() || thinking}
            onClick={() => send(undefined, true)}
            title="Deliver this message to the client (a deliberate, audited act)"
          >
            Send to client →
          </button>
        )}
      </div>
      <div className="cs-foot">
        {isTeamView ? (
          <span className="cs-defnote">
            Messages are <b>team-only</b> by default. Use <b>“Send to client →”</b>{' '}
            to deliver one — nothing reaches the client by accident.
          </span>
        ) : (
          <span />
        )}
        <span>Enter to send · Shift+Enter newline</span>
      </div>
    </section>
  );
}
